import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Tag } from "Components/Analyses/_Shared/Tag";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as SearchIcon } from "Components/_Shared/Icons/Assets/search.svg";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { getListCollaboratorsQueryKey, useAddCollaborator, useSearchCollaboratorBySsoId } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AddCollaboratorBadRequestErrorCode,
  AddCollaboratorNotFoundErrorCode,
  SearchAgentNotFoundErrorCode,
} from "Infrastructure/ApiZfp/Schema";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FormEvent, useState } from "react";
import { SHARING_ADVISORS_MAX_COUNT } from "../../constants";
import { getAgentFullName } from "./utils";

const SSO_ID_MIN_LENGTH = 5;
const SSO_ID_MAX_LENGTH = 6;
// long enough for the advisor to type the optional 6th digit before a 5-digit search fires
const SEARCH_DEBOUNCE_MS = 500;

type Props = {
  readonly addedAdvisorSsoIds: string[];
};

export const SharingAdvisorSearch = ({ addedAdvisorSsoIds }: Props) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const [ssoId, setSsoId] = useState("");
  const [addError, setAddError] = useState<string>();
  const debouncedSsoId = useDebounce(ssoId, SEARCH_DEBOUNCE_MS);

  const {
    data: advisor,
    isFetching,
    error: searchError,
  } = useSearchCollaboratorBySsoId(
    { ssoId: debouncedSsoId },
    { query: { enabled: debouncedSsoId.length >= SSO_ID_MIN_LENGTH, retry: false } },
  );

  const { mutate: addCollaborator, isPending: isAdding } = useAddCollaborator({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getListCollaboratorsQueryKey() });
        setSsoId("");
      },
      onError: (error) => {
        switch (error?.code) {
          case AddCollaboratorNotFoundErrorCode.agent_not_found:
            setAddError(t(Resources.AnalysisDetail.Sharing.AddAdvisor.NotFound));
            break;
          case AddCollaboratorBadRequestErrorCode.collaborator_limit_reached:
            setAddError(t(Resources.AnalysisDetail.Sharing.AddAdvisor.LimitReached, { max: SHARING_ADVISORS_MAX_COUNT }));
            break;
          case AddCollaboratorBadRequestErrorCode.cannot_collaborate_with_self:
            setAddError(t(Resources.AnalysisDetail.Sharing.AddAdvisor.CannotAddSelf));
            break;
          default:
            setAddError(t(Resources.AnalysisDetail.Toast.ActionError));
        }
      },
    },
  });

  const isComplete = ssoId.length >= SSO_ID_MIN_LENGTH;
  const isSearching = isComplete && (ssoId !== debouncedSsoId || isFetching);
  const foundAdvisor = isComplete && !isSearching ? advisor : undefined;
  const isAlreadyAdded = !!foundAdvisor && addedAdvisorSsoIds.includes(foundAdvisor.ssoId);

  const getSearchErrorMessage = () => {
    if (!isComplete || isSearching || !searchError) {
      return undefined;
    }

    return searchError.code === SearchAgentNotFoundErrorCode.agent_not_found
      ? t(Resources.AnalysisDetail.Sharing.AddAdvisor.NotFound)
      : t(Resources.AnalysisDetail.Toast.ActionError);
  };

  const errorMessage = addError ?? getSearchErrorMessage();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!foundAdvisor || isAlreadyAdded || isAdding) return;

    setAddError(undefined);
    addCollaborator({ data: { userGuid: foundAdvisor.userGuid } });
  };

  const renderResult = () => {
    if (isSearching) {
      return <CircularProgress size={20} />;
    }

    if (errorMessage) {
      return (
        <HStack role="alert" alignItems="center" spacing={Spacing.S} minWidth={0} color="error.main">
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
          <Typography fontSize={13} color="inherit">
            {errorMessage}
          </Typography>
        </HStack>
      );
    }

    if (!foundAdvisor) {
      return null;
    }

    const fullName = getAgentFullName(foundAdvisor);

    return (
      <HStack alignItems="center" spacing={Spacing.M} minWidth={0}>
        <UserInitialsAvatar firstName={foundAdvisor.firstName} surname={foundAdvisor.lastName} variant="small" />
        <Typography variant="body2" fontWeight={500} noWrap>
          {fullName}
        </Typography>
        {isAlreadyAdded ? (
          <Tag variant="solid" color="grey" title={t(Resources.AnalysisDetail.Sharing.AddAdvisor.InList)} />
        ) : (
          <Button
            type="submit"
            variant="text"
            loading={isAdding}
            startAdornment={<AddIcon />}
            sx={{ flexShrink: 0, "& .MuiTypography-root": { fontWeight: 700 } }}
          >
            {t(Resources.AnalysisDetail.Sharing.AddAdvisor.Add)}
          </Button>
        )}
      </HStack>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <HStack spacing={Spacing.L} alignItems="center">
        <Box width={340} flexShrink={0}>
          <TextInput
            inputStartAdornment={<SearchIcon />}
            placeholder={t(Resources.AnalysisDetail.Sharing.AddAdvisor.Placeholder)}
            value={ssoId}
            autoComplete="off"
            maxLength={SSO_ID_MAX_LENGTH}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            // no message on purpose - the error is rendered next to the field, the input only gets the error border
            fieldError={errorMessage ? { type: "manual" } : undefined}
            onInputChange={(event) => {
              setSsoId(event.target.value.replace(/\D/g, ""));
              setAddError(undefined);
            }}
          />
        </Box>
        {/* same height as the input, so the result lines up with it and never shifts the layout */}
        <Flex flex={1} minWidth={0} height={40} alignItems="center">
          {renderResult()}
        </Flex>
      </HStack>
    </form>
  );
};
